<template>
  <div>
    <div v-if="formattedData.length >= 10000" class="card-panel red lighten-4"><h6>Too much data to plot</h6></div>
    <HighChart v-else :options="chartOptions"/>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import moment from 'moment'
import Highcharts from 'highcharts'
// import SwitchApi from '../../../services/api/switch/SwitchApi'

export default {
  name: 'Histogram',
  components: {
    HighChart: Chart
  },
  props: {
    railObject: {
      required: true
    },
    apiData: {
      required: true
    },
    metric: {
      required: true
    },
    metricTranslated: {
      required: true
    },
    unit: {
      required: true
    },
    binSize: {
      required: true
    }
  },
  data () {
    return {
      chartOptions: {
        chart: {
          type: 'column',
          zoomType: 'xy',
          height: 300,
          resetZoomButton: {
            position: {
              x: 0,
              y: -10
            }
          },
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        title: {
          text: 'Object Name'
        },
        accessibility: {
          enabled: false
        },
        credits: {
          enabled: false
        },
        subtitle: {
          text: 'Histogram'
        },
        lang: {
          noData: this.$t('charts.nodata')
        },
        legend: {
          enabled: true
        },
        xAxis: {
          title: {
            text: 'metric'
          },
          type: this.getXAxisType(this.metric),
          plotLines: []
        },
        yAxis: {
          title: {
            text: this.getTranslationKey('frequency')
          }
        },
        plotOptions: {
          series: {
            events: {
              show: (e) => {
                const dir = parseInt(e.target.userOptions.dir)
                const eng = e.target.userOptions.eng
                this.enabled.push({dir, eng})
              },
              hide: (e) => {
                const dir = parseInt(e.target.userOptions.dir)
                const eng = e.target.userOptions.eng
                this.enabled.splice(this.enabled.findIndex(x => x.dir === dir && x.eng === eng), 1)
              }
            }
          },
          area: {
            stacking: 'normal',
            marker: {
              radius: 5,
              states: {
                hover: {
                  enabled: false
                }
              }
            },
            states: {
              hover: {
                marker: {
                  enabled: false
                }
              }
            }
          },
          column: {
            stacking: 'normal',
            pointPadding: 0,
            borderWidth: 0,
            groupPadding: 0,
            shadow: false
          }
        },
        tooltip: {
          enabled: true
        },
        series: [
          {
            name: 'histogram',
            marker: {
              enabled: false
            },
            data: [],
            turboThreshold: 10000
          }
        ]
      },
      referenceData: {},
      formattedData: [{}, {}],
      median: null,
      enabled: []
    }
  },
  async mounted () {},
  methods: {
    // Do not show reference data in POSS 7.7-05 and higher
    // PO-920
    // async getRefData (direction, engine) {
    //   if (this.railObject) {
    //     await SwitchApi.getReferenceData(
    //       this.railObject.RailObjectId,
    //       direction,
    //       parseInt(engine))
    //       .then(result => {
    //         this.referenceData = result.data
    //       })
    //   }
    // },
    formatChartData () {
      if (this.apiData !== null) {
        const xx = this.metric
        let formattedData = [{}, {}]
        this.apiData.forEach(m => {
          while (!formattedData[m.Direction]) {
            formattedData.push({})
          }
          if (!formattedData[m.Direction][m.Engine]) {
            formattedData[m.Direction][m.Engine] = []
          }
          const bin = Math.floor(m[xx] / this.binSize) * this.binSize
          let index = formattedData[m.Direction][m.Engine].findIndex(x => x.x === bin)
          if (index === -1) {
            index = formattedData[m.Direction][m.Engine].push({
              x: bin,
              y: 0
            }) - 1
          }
          if (this.metric === 'Date') {
            let mdt = moment(formattedData[m.Direction][m.Engine][index].x, 'YYYYMMDD').valueOf()
            formattedData[m.Direction][m.Engine][index].x = mdt
            formattedData[m.Direction][m.Engine][index].y = formattedData[m.Direction][m.Engine][index].y + 1
          } else {
            formattedData[m.Direction][m.Engine][index].y = formattedData[m.Direction][m.Engine][index].y + 1
          }
        })
        // sort all Data
        let dir = 0
        formattedData.forEach(m => {
          for (let eng in m) {
            formattedData[dir][eng] = formattedData[dir][eng].sort((x, y) => (x.x - y.x))
          }
          dir++
        })
        this.formattedData = formattedData
      }
    },
    async setChartData () {
      const directions = ['N', 'R']
      const alarmLabels = ['None', 'Normal', 'Warning', 'Alarm']
      await this.formatChartData()
      let series = []
      let enabled = []
      let dir = 0
      this.formattedData.forEach(m => {
        for (let eng in m) {
          series.push({
            name: 'direction: ' + directions[dir] + ', engine: ' + eng,
            dir: dir,
            eng: eng,
            marker: {
              enabled: false
            },
            data: this.formattedData[dir][eng],
            turboThreshold: 10000
          })
          enabled.push({dir: parseInt(dir), eng: eng})
        }
        dir++
      })
      this.chartOptions.series = series
      if (this.enabled.length === 0) {
        this.enabled = enabled
      }
      this.chartOptions.xAxis = {
        type: this.getXAxisType(this.metric),
        min: null,
        max: null,
        // Leon: 20230706: Ik heb onderstaande formatter uitgezet. Dit zorgde voor (een) probleem bij de histogram op basis van tijd.
        // Met de formatter werd de unixtimestamp weergegeven. Voor Date een paar meer fixes door moeten voeren zie de if statements in Histogram*.vue
        // Een probleem was dat highcharts niet door had dat het een datum was, in de grafiek ontstonden zo gaten bij 40e, 50e, 60e dag van de maand. enz.
        // labels: {
        //  formatter () {
        //    return this.value
        //  }
        // },
        plotLines: [],
        title: {
          text: this.metricTranslated + ' ' + this.unit
        }
      }
      if (this.metric === 'Alarm') {
        this.chartOptions.xAxis.categories = alarmLabels
      } else {
        this.chartOptions.xAxis.categories = null
      }
      this.chartOptions.title.text = this.railObject.ComplexName + ' ' + this.railObject.Name
      this.chartOptions.subtitle.text = this.metricTranslated
    },
    getMedian: function (dir, eng) {
      const data = this.formattedData[dir][eng]
      let dataSize = 0
      data.forEach(d => {
        dataSize += d.y
      })
      const middle = dataSize / 2
      let counter = 0
      let value = 0
      data.forEach(x => {
        if (counter < middle) {
          value = x.x
          counter += x.y
        }
      })
      this.median = value
    },
    drawPlotLines: function () {
      let dir = this.enabled[0].dir
      let eng = this.enabled[0].eng
      this.drawMedian(dir, eng)
      // this.drawReference(dir, eng)
    },
    clearPlotLines: function () {
      this.chartOptions.xAxis.plotLines = []
    },
    drawMedian: async function (dir, eng) {
      await this.getMedian(dir, eng)
      this.chartOptions.xAxis.plotLines.push({
        color: 'red',
        value: this.median,
        label: {
          text: 'median'
        },
        width: 2,
        zIndex: 10
      })
    },
    // drawReference, do not show reference anymore in POSS 7.7-05 and higher
    // PO-920
    // drawReference: async function (dir, eng) {
    //   await this.getRefData(dir, eng)
    //   this.chartOptions.xAxis.plotLines.push({
    //     color: 'blue',
    //     value: this.referenceData[this.metric],
    //     label: {
    //       text: 'reference'
    //     },
    //     width: 2,
    //     zIndex: 10
    //   })
    // },
    getXAxisType (val) {
      if (val === 'Date') {
        return 'datetime'
      } else {
        return 'linear'
      }
    },
    getTranslationKey (val) {
      switch (val) {
        case 'frequency':
          return this.$t('views.switch.frequency')
        default:
          return val
      }
    }
  },
  watch: {
    apiData (val) {
      if (val) {
        this.setChartData()
      }
    },
    /* async direction (newValue) {
      await this.getApiData()
      this.formatChartData()
    }, */
    /* async engine (newValue) {
      await this.getApiData()
      this.formatChartData()
    }, */
    async metric (newValue) {
      if (this.apiData[0].hasOwnProperty(newValue)) {
        this.setChartData()
      }
    },
    async binSize (newValue) {
      this.setChartData()
    },
    async enabled (newValue) {
      this.$emit('on-select', newValue)
      if (newValue.length === 1) {
        this.drawPlotLines()
      } else {
        this.clearPlotLines()
      }
    }
  }
}
</script>

<style scoped>
  .title {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 23px !important;
    color: rgba(0, 0, 0, 0.87);
  }
</style>



// WEBPACK FOOTER //
// src/components/switch/analysis/Histogram.vue